// Statistik Page - Phase 4 Re-Design
// - Jahr = Standard-Ansicht, Monat per Toggle
// - Dynamische Fahrzeug-Spalten (nur reale Fahrzeuge der Wache)
// - Sortierung: NotSan/RettAss zuerst (nach Dienstgrad), dann andere
// - Expandable Rows: Klick → Funktions-Aufschlüsselung pro Fahrzeug
// - Wandern als eigene Spalte (Stück + %)
// - Mitarbeiter-Fokus (keine Wachen-Charts)
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PersonalStatistics from '../components/statistik/PersonalStatistics';
import {
  BarChart3, Download, Users, ChevronDown, ChevronRight, Calendar,
  Sun, Moon,
} from 'lucide-react';
import { toast } from 'sonner';
import { statistikenApi, wacheApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { downloadBlob, cn } from '../lib/utils';
import { useFahrzeugBilder } from '../lib/fahrzeugBilder';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { getFahrzeugFarbe } from '../components/einteilung/constants';

const buildRange = (mode, year, month) => {
  if (mode === 'jahr') {
    return { von: `${year}-01-01`, bis: `${year}-12-31`, label: `${year}` };
  }
  const m = String(month + 1).padStart(2, '0');
  const last = new Date(year, month + 1, 0).getDate();
  return {
    von: `${year}-${m}-01`,
    bis: `${year}-${m}-${String(last).padStart(2, '0')}`,
    label: new Date(year, month, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
  };
};

const pct = (n) => (n != null ? `${Number(n).toFixed(1)}%` : '—');

// LHF-Sub-Kategorien: StF | AT (ATF+ATM) | WT (WTF+WTM) | MA
// Die Kürzel werden case-insensitive verglichen (Daten enthalten teilweise "Ma" statt "MA")
// Import-Kürzel (Sammelbezeichnungen aus historischen Dienstplänen) werden mit-aggregiert:
//   AT-Trupp gesamt = ATF + ATM + AT
//   WT-Trupp gesamt = WTF + WTM + WT
// Damit fließen Alt-Daten nahtlos in dieselbe LHF-Sub-Statistik ein.
const LHF_GRUPPEN = [
  { key: 'StF', label: 'StF', kuerzel: ['STF'], beschreibung: 'Staffelführer' },
  { key: 'AT',  label: 'AT',  kuerzel: ['ATF', 'ATM', 'AT'], beschreibung: 'Angriffstrupp (ATF + ATM, inkl. Import „AT")' },
  { key: 'WT',  label: 'WT',  kuerzel: ['WTF', 'WTM', 'WT'], beschreibung: 'Wassertrupp (WTF + WTM, inkl. Import „WT")' },
  { key: 'MA',  label: 'MA',  kuerzel: ['MA'], beschreibung: 'Maschinist' },
];

// Kategorie-Definitionen (Reihenfolge = Anzeige-Reihenfolge in Tabelle)
const KATEGORIEN = [
  {
    key: 'RD',
    label: 'Rettungsdienst',
    short: 'RD',
    // Dezente Hintergrundtönung
    bgHeader: 'bg-rose-50',
    bgCol: 'bg-rose-50/30',
    bgColAlt: 'bg-rose-50/15',
    border: 'border-rose-200',
    accent: 'text-rose-700',
  },
  {
    key: 'LHF',
    label: 'Löschfahrzeug',
    short: 'LHF',
    bgHeader: 'bg-sky-50',
    bgCol: 'bg-sky-50/30',
    bgColAlt: 'bg-sky-50/15',
    border: 'border-sky-200',
    accent: 'text-sky-700',
  },
  {
    key: 'Sonstige',
    label: 'Sonstige',
    short: 'Sonst.',
    bgHeader: 'bg-stone-100',
    bgCol: 'bg-stone-50/40',
    bgColAlt: 'bg-stone-50/20',
    border: 'border-stone-300',
    accent: 'text-stone-700',
  },
];
const KAT_BY_KEY = Object.fromEntries(KATEGORIEN.map(k => [k.key, k]));

// Aggregiert die Anzahl + Tag/Nacht für eine Gruppe von Funktions-Kürzeln (case-insensitive)
const aggregateFunktionen = (fz, kuerzelListe) => {
  const upper = kuerzelListe.map(k => k.toUpperCase());
  const fns = (fz?.funktionen || []).filter(f => upper.includes((f.kuerzel || '').toUpperCase()));
  return fns.reduce((acc, f) => ({
    anzahl: acc.anzahl + (f.anzahl_gesamt || 0),
    tag:    acc.tag + (f.haupt_tag || 0) + (f.springer_tag || 0),
    nacht:  acc.nacht + (f.haupt_nacht || 0) + (f.springer_nacht || 0),
  }), { anzahl: 0, tag: 0, nacht: 0 });
};

// Gruppiert Fahrzeuge nach Backend-Kategorie. Springer-Fahrzeuge hängen an LHF.
// Sortierung innerhalb einer Kategorie: feste Fahrzeuge zuerst, Springer ans Ende.
const groupFahrzeugeByKategorie = (fahrzeuge) => {
  const groups = { RD: [], LHF: [], Sonstige: [] };
  (fahrzeuge || []).forEach(f => {
    const k = f.kategorie || 'Sonstige';
    if (groups[k]) groups[k].push(f);
    else groups.Sonstige.push(f);
  });
  // Sortierung pro Kategorie: feste zuerst, dann Springer; alphanumerisch
  const cmp = (a, b) => {
    const sa = a.ist_springer ? 1 : 0;
    const sb = b.ist_springer ? 1 : 0;
    if (sa !== sb) return sa - sb;
    return (a.kurzname || '').localeCompare(b.kurzname || '', 'de', { numeric: true });
  };
  Object.values(groups).forEach(arr => arr.sort(cmp));
  return groups;
};

export function StatistikPage() {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPersonal = searchParams.get('ansicht') !== 'gesamt';
  const [loadError, setLoadError] = useState(false);
  const [abteilungen, setAbteilungen] = useState([]);
  const [selectedAbteilung, setSelectedAbteilung] = useState('');
  const [zeitraumMode, setZeitraumMode] = useState('jahr'); // 'monat' | 'jahr' — Jahr ist Default
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth());
  const [data, setData] = useState({ fahrzeuge: [], mitarbeiter: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const canSeeAllAbteilungen = ['DEVELOPER', 'WACHLEITUNG'].includes(user?.rolle);
  const range = useMemo(
    () => buildRange(zeitraumMode, selectedYear, selectedMonth),
    [zeitraumMode, selectedYear, selectedMonth]
  );

  useEffect(() => {
    const loadAbteilungen = async () => {
      try {
        const res = await wacheApi.getAbteilungen();
        setAbteilungen(res.data);
        if (res.data.length > 0) {
          setSelectedAbteilung(user?.abteilung_id || res.data[0].id);
        }
      } catch (error) {
        console.error('Failed to load abteilungen:', error);
      }
    };
    if (!isPersonal) loadAbteilungen();
  }, [user, isPersonal]);

  useEffect(() => {
    let active = true;
    setData({ fahrzeuge: [], mitarbeiter: [] });
    setLoadError(false);
    if (!user?.id || (!isPersonal && !selectedAbteilung)) {
      setIsLoading(false);
      return () => { active = false; };
    }
    setIsLoading(true);
    statistikenApi.getUebersicht({
      von_datum: range.von,
      bis_datum: range.bis,
      abteilung_id: isPersonal ? user.abteilung_id : selectedAbteilung,
    }).then(res => {
      if (active) setData(res.data || { fahrzeuge: [], mitarbeiter: [] });
    }).catch(() => {
      if (active) setLoadError(true);
    }).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => { active = false; };
  }, [isPersonal, user?.id, user?.abteilung_id, selectedAbteilung, range.von, range.bis]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await statistikenApi.exportCsv({
        von_datum: range.von,
        bis_datum: range.bis,
        abteilung_id: selectedAbteilung,
      });
      // res.data ist ein Blob, da api.js responseType:'blob' setzt
      const blob = res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const fname = zeitraumMode === 'jahr'
        ? `statistik_${selectedYear}.csv`
        : `statistik_${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}.csv`;
      downloadBlob(blob, fname);
      toast.success('Export erfolgreich');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Export fehlgeschlagen: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // KPI-Aggregate entfernt — User wollte schlanken Mitarbeiter-Fokus

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return [current + 1, current, current - 1, current - 2, current - 3];
  }, []);

  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: new Date(2000, i, 1).toLocaleDateString('de-DE', { month: 'long' }),
  })), []);

  const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));

  return (
    <MainLayout>
      <TooltipProvider>
        <div className="space-y-5" data-testid="statistics-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold">{isPersonal ? 'Meine Statistik' : 'Statistik gesamt'}</h1>
              <p className="text-sm text-muted-foreground">
                {isPersonal ? 'Deine Dienste, Fahrzeuge und Funktionen im gewählten Zeitraum' : 'Einsätze pro Fahrzeug und Funktion · Springer-Einsätze inklusive'}
              </p>
            </div>
            {!isPersonal && <Button
              onClick={handleExport}
              disabled={isExporting || (data.mitarbeiter || []).length === 0}
              data-testid="export-btn"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exportiere...' : 'CSV Export'}
            </Button>}
          </div>

          <nav aria-label="Statistikansicht" className="flex flex-wrap gap-2">
            <Button variant={isPersonal ? 'default' : 'outline'} aria-pressed={isPersonal}
              onClick={() => setSearchParams({})}>Meine Statistik</Button>
            <Button variant={!isPersonal ? 'default' : 'outline'} aria-pressed={!isPersonal}
              onClick={() => setSearchParams({ ansicht: 'gesamt' })}>Statistik gesamt</Button>
          </nav>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                {!isPersonal && canSeeAllAbteilungen && (
                  <div className="w-48">
                    <label className="text-xs font-medium mb-1 block">Abteilung</label>
                    <Select value={selectedAbteilung} onValueChange={setSelectedAbteilung}>
                      <SelectTrigger data-testid="abteilung-select">
                        <SelectValue placeholder="Abteilung wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {abteilungen.map(abt => (
                          <SelectItem key={abt.id} value={abt.id}>{abt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex gap-1" data-testid="zeitraum-mode-toggle">
                  <Button
                    variant={zeitraumMode === 'jahr' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setZeitraumMode('jahr')}
                    data-testid="zeitraum-jahr"
                    className="gap-1"
                  >
                    <Calendar className="w-3 h-3" />Jahr
                  </Button>
                  <Button
                    variant={zeitraumMode === 'monat' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setZeitraumMode('monat')}
                    data-testid="zeitraum-monat"
                  >Monat</Button>
                </div>

                <div className="w-32">
                  <label className="text-xs font-medium mb-1 block">Jahr</label>
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {yearOptions.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {zeitraumMode === 'monat' && (
                  <div className="w-44">
                    <label className="text-xs font-medium mb-1 block">Monat</label>
                    <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger data-testid="month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Badge variant="outline" className="ml-auto text-sm" data-testid="zeitraum-label">
                  {range.label}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Loading / Empty */}
          {loadError ? (
            <Card><CardContent className="py-10 text-center" role="alert">
              Statistik konnte nicht geladen werden. Bitte die Seite erneut laden.
            </CardContent></Card>
          ) : isLoading ? (
            <div className="grid gap-3 sm:grid-cols-4">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : isPersonal ? (
            <PersonalStatistics statistik={(data.mitarbeiter || []).find(row => row.id === user?.id)} fahrzeuge={data.fahrzeuge} />
          ) : (data.mitarbeiter || []).length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Keine Daten vorhanden</p>
                <p className="text-muted-foreground">Für diesen Zeitraum sind keine Einteilungen erfasst.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Detailtabelle */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="w-5 h-5" /> Detailtabelle ({range.label})
                  </CardTitle>
                  <CardDescription>
                    Klick auf Zeile → Aufschlüsselung der Funktionen pro Fahrzeug.
                    Sortiert: NotSan/RettAss oben, dann nach Dienstgrad.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0 sm:px-2">
                  <div className="rounded-md border overflow-x-auto">
                    {(() => {
                      const groups = groupFahrzeugeByKategorie(data.fahrzeuge || []);
                      // Berechne pro FZ, ob global irgendwer feste oder Springer-Einsätze hatte.
                      // Basis für die Spalten-Sichtbarkeit:
                      //  - Feste-Spalte: FZ ist kein reines Springer-FZ ODER irgendwer war fest darauf
                      //  - Springer-Spalte: irgendwer hatte Springer-Einsätze dort
                      // Beispiel: DLK (Sollstärke 0, nicht ist_springer) mit 2× fest + 3× Springer
                      // erzeugt DLK-Feste-Spalte (in Sonstige) UND DLK-Springer-Spalte (LHF-Springer-Block).
                      const anyHaupt = {};
                      const anySpringer = {};
                      (data.mitarbeiter || []).forEach(row => {
                        (row.fahrzeuge || []).forEach(fz => {
                          if ((fz.anzahl_haupt || 0) > 0) anyHaupt[fz.fahrzeug_id] = true;
                          if ((fz.anzahl_springer || 0) > 0) anySpringer[fz.fahrzeug_id] = true;
                        });
                      });
                      const hasHauptCol = (fz) => !fz.ist_springer || !!anyHaupt[fz.id];
                      const hasSprCol = (fz) => !!anySpringer[fz.id];

                      const rdFzList = groups.RD.filter(hasHauptCol);
                      const lhfFzList = groups.LHF.filter(f => !f.ist_springer && hasHauptCol(f));
                      const sonstigeFzList = groups.Sonstige.filter(hasHauptCol);
                      // LHF-Sub-Funktionen (StF/AT/WT/MA) NUR bei echten Löschfahrzeug-Typen zeigen.
                      // Sonstige Fahrzeuge in der LHF-Kategorie (z.B. wenn jemand einen
                      // Override gesetzt hat) bekommen nur die Feste-Spalte.
                      const LOESCH_TYPEN = new Set(['LHF', 'HLF', 'TLF']);
                      const isEchtesLoeschfz = (fz) => LOESCH_TYPEN.has((fz.typ || '').toUpperCase());
                      // Springer-Block: alle Fahrzeuge (kategorie-übergreifend) mit Springer-Einsätzen,
                      // Springer-FZ zuerst, dann alphabetisch.
                      const springerFzList = (data.fahrzeuge || [])
                        .filter(hasSprCol)
                        .sort((a, b) => {
                          const sa = a.ist_springer ? 0 : 1;
                          const sb = b.ist_springer ? 0 : 1;
                          if (sa !== sb) return sa - sb;
                          return (a.kurzname || '').localeCompare(b.kurzname || '', 'de', { numeric: true });
                        });

                      // Spaltenzahl pro Kategorie:
                      //  RD: Aggregat-Spalte (%RD) + 1 Spalte je RTW
                      //  LHF: je echtes Löschfz (1 + LHF_GRUPPEN.length), je nicht-Löschfz nur 1 + 1 je Springer-Spalte (kategorieübergreifend)
                      //  Sonstige: 1 Spalte je Fz
                      const rdCols = 1 + rdFzList.length;
                      const lhfSubCols = lhfFzList.reduce(
                        (sum, fz) => sum + (isEchtesLoeschfz(fz) ? (1 + LHF_GRUPPEN.length) : 1),
                        0
                      );
                      const lhfCols = lhfSubCols + springerFzList.length;
                      const sonstigeCols = sonstigeFzList.length;
                      const totalCols = 1 /*chevron*/ + 1 /*Name*/ + 1 /*DG*/ + 1 /*Quali*/ + 1 /*Σ*/ + rdCols + lhfCols + sonstigeCols + 1 /*WA*/ + 1 /*RW*/ + 1 /*ZBV*/;

                      const KAT_RD = KAT_BY_KEY.RD;
                      const KAT_LHF = KAT_BY_KEY.LHF;
                      const KAT_SONSTIGE = KAT_BY_KEY.Sonstige;
                      const SPR = 'bg-orange-50';            // Springer-Spalte Header
                      const SPR_CELL = 'bg-orange-50/40';    // Springer-Spalte Zellen

                      return (
                    <Table data-testid="statistik-table">
                      <TableHeader>
                        {/* Kategorie-Header-Zeile (gruppiert über Spaltengruppen) */}
                        <TableRow className="border-b-0">
                          <TableHead className="sticky left-0 bg-background z-10" colSpan={5}></TableHead>
                          <TableHead colSpan={rdCols} className={cn('text-center font-bold text-xs uppercase tracking-wider', KAT_RD.bgHeader, KAT_RD.accent, 'border-l border-l-2', KAT_RD.border)}>
                            {KAT_RD.label}
                          </TableHead>
                          <TableHead colSpan={lhfCols} className={cn('text-center font-bold text-xs uppercase tracking-wider', KAT_LHF.bgHeader, KAT_LHF.accent, 'border-l border-l-2', KAT_LHF.border)}>
                            {KAT_LHF.label}
                          </TableHead>
                          {sonstigeFzList.length > 0 && (
                            <TableHead colSpan={sonstigeCols} className={cn('text-center font-bold text-xs uppercase tracking-wider', KAT_SONSTIGE.bgHeader, KAT_SONSTIGE.accent, 'border-l border-l-2', KAT_SONSTIGE.border)}>
                              {KAT_SONSTIGE.label}
                            </TableHead>
                          )}
                          <TableHead className="bg-pink-50 border-l border-l-2 border-pink-200 text-center text-xs font-bold uppercase tracking-wider text-pink-700">WA</TableHead>
                          <TableHead className="bg-rose-100 border-l border-rose-300 text-center text-xs font-bold uppercase tracking-wider text-rose-800">RW</TableHead>
                          <TableHead className="bg-slate-100 border-l border-slate-300 text-center text-xs font-bold uppercase tracking-wider text-slate-700">ZBV</TableHead>
                        </TableRow>
                        {/* Spalten-Header-Zeile */}
                        <TableRow>
                          <TableHead className="w-8 sticky left-0 bg-background z-10"></TableHead>
                          <TableHead className="sticky left-8 bg-background z-10 min-w-[160px]">Name</TableHead>
                          <TableHead className="w-14">DG</TableHead>
                          <TableHead>Qual.</TableHead>
                          <TableHead className="text-right">
                            <Tooltip>
                              <TooltipTrigger className="font-semibold">Σ</TooltipTrigger>
                              <TooltipContent>Gesamtdienste (Haupt- + Springer-Einsätze)</TooltipContent>
                            </Tooltip>
                          </TableHead>
                          {/* RD-Aggregat + RTWs */}
                          <TableHead className={cn('text-right whitespace-nowrap font-semibold border-l border-l-2', KAT_RD.border, KAT_RD.bgCol, KAT_RD.accent)}>
                            <Tooltip>
                              <TooltipTrigger>%RD</TooltipTrigger>
                              <TooltipContent>Anteil Rettungsdienst (Hauptfunktion auf RTW/NEF/KTW)</TooltipContent>
                            </Tooltip>
                          </TableHead>
                          {rdFzList.map(fz => (
                            <TableHead key={fz.id} className={cn('text-right whitespace-nowrap', KAT_RD.bgColAlt)}>
                              <Tooltip>
                                <TooltipTrigger className="font-mono">{fz.kurzname}</TooltipTrigger>
                                <TooltipContent>{fz.funkrufname}</TooltipContent>
                              </Tooltip>
                            </TableHead>
                          ))}
                          {/* LHF: Sub-Spalten StF / AT / WT / MA nur für echte Löschfz;
                              Nicht-Löschfz in LHF-Kategorie erhalten nur ihre Feste-Spalte. */}
                          {lhfFzList.map((fz, idx) => (
                            <React.Fragment key={fz.id}>
                              <TableHead className={cn('text-right whitespace-nowrap font-semibold', KAT_LHF.bgCol, idx === 0 && 'border-l border-l-2', idx === 0 && KAT_LHF.border)}>
                                <Tooltip>
                                  <TooltipTrigger className="font-mono">{fz.kurzname}</TooltipTrigger>
                                  <TooltipContent>{fz.funkrufname} · Gesamt</TooltipContent>
                                </Tooltip>
                              </TableHead>
                              {isEchtesLoeschfz(fz) && LHF_GRUPPEN.map(g => (
                                <TableHead key={`${fz.id}-${g.key}`} className={cn('text-right whitespace-nowrap', KAT_LHF.bgColAlt)}>
                                  <Tooltip>
                                    <TooltipTrigger className="font-mono text-[11px]">{g.label}</TooltipTrigger>
                                    <TooltipContent>{fz.funkrufname} · {g.beschreibung}</TooltipContent>
                                  </Tooltip>
                                </TableHead>
                              ))}
                            </React.Fragment>
                          ))}
                          {/* Springer-Spalten (kategorieübergreifend, orange Akzent) — feste Springer-FZ + Fahrzeuge mit Springer-Einsätzen */}
                          {springerFzList.map(fz => (
                            <TableHead key={`spr-${fz.id}`} className={cn('text-right whitespace-nowrap border-l', SPR)}>
                              <Tooltip>
                                <TooltipTrigger className="font-mono text-orange-700 font-semibold">
                                  {fz.kurzname}<span className="opacity-60">⇢</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="font-semibold">{fz.funkrufname} · Springer</p>
                                  <p className="text-xs">Wie oft dieses Fahrzeug beim LHF-Dienst zusätzlich besprungen wurde.</p>
                                  <p className="text-xs text-muted-foreground">Nenner = LHF-Hauptdienste</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableHead>
                          ))}
                          {/* Sonstige — direkt die Fahrzeuge, kein Aggregat-%Spalte */}
                          {sonstigeFzList.map((fz, idx) => (
                            <TableHead key={fz.id} className={cn('text-right whitespace-nowrap', KAT_SONSTIGE.bgColAlt, idx === 0 && 'border-l border-l-2', idx === 0 && KAT_SONSTIGE.border)}>
                              <Tooltip>
                                <TooltipTrigger className="font-mono">{fz.kurzname}</TooltipTrigger>
                                <TooltipContent>{fz.funkrufname}</TooltipContent>
                              </Tooltip>
                            </TableHead>
                          ))}
                          <TableHead className="text-right bg-pink-50/40 border-l border-l-2 border-pink-200">WA</TableHead>
                          <TableHead className="text-right bg-rose-100/40 border-l border-rose-300">RW</TableHead>
                          <TableHead className="text-right bg-slate-100/40 border-l border-slate-300">ZBV</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(data.mitarbeiter || []).map((row, idx, arr) => {
                          const prev = idx > 0 ? arr[idx - 1] : null;
                          // Trennlinie zwischen NotSan-Block und Rest
                          // (NotSan ist backend-seitig nach oben sortiert).
                          const istErsterNichtNotSan =
                            prev?.qualifikation === 'NotSan' &&
                            row.qualifikation !== 'NotSan';
                          const fzMap = Object.fromEntries((row.fahrzeuge || []).map(fz => [fz.fahrzeug_id, fz]));
                          const renderFzCell = (fz, extraClass = '', mode = 'auto') => {
                            const rfz = fzMap[fz.id];
                            // mode = 'auto'      → Standard-Metrik (Backend-primary, wie bisher)
                            //        'haupt'     → Feste Einteilungen (anzahl_haupt / prozent_haupt_gesamt)
                            //        'springer'  → Springer-Einsätze  (anzahl_springer / prozent_springer_gesamt)
                            let count, tag, nacht, pctVal, tagPct, nachtPct, label;
                            if (rfz) {
                              if (mode === 'haupt') {
                                count = rfz.anzahl_haupt || 0;
                                tag   = rfz.haupt_tag   || 0;
                                nacht = rfz.haupt_nacht || 0;
                                pctVal = rfz.prozent_haupt_gesamt;
                                tagPct = rfz.prozent_haupt_tag;
                                nachtPct = rfz.prozent_haupt_nacht;
                                label = `${fz.funkrufname} · Fest`;
                              } else if (mode === 'springer') {
                                count = rfz.anzahl_springer || 0;
                                tag   = rfz.springer_tag   || 0;
                                nacht = rfz.springer_nacht || 0;
                                pctVal = rfz.prozent_springer_gesamt;
                                tagPct = rfz.prozent_springer_tag;
                                nachtPct = rfz.prozent_springer_nacht;
                                label = `${fz.funkrufname} · Springer`;
                              } else {
                                count = rfz.anzahl_gesamt;
                                tag   = rfz.anzahl_tag;
                                nacht = rfz.anzahl_nacht;
                                pctVal = rfz.prozent_gesamt;
                                tagPct = rfz.prozent_tag;
                                nachtPct = rfz.prozent_nacht;
                                label = fz.funkrufname + (fz.ist_springer ? ' (Springer)' : '');
                              }
                            }
                            return (
                              <TableCell key={`${fz.id}-${mode}`} className={cn('text-right font-mono text-xs', extraClass)}>
                                {rfz && count > 0 ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <div className="font-semibold">{pct(pctVal)}</div>
                                        <div className="text-[10px] text-muted-foreground">
                                          <span className="text-amber-600">{tag}</span>/<span className="text-indigo-600">{nacht}</span>
                                        </div>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs font-semibold">{label}</p>
                                      <p className="text-xs">{count}× ({pct(pctVal)})</p>
                                      <p className="text-xs">Tag {tag} · Nacht {nacht}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : '—'}
                              </TableCell>
                            );
                          };
                          const renderLhfGroupCell = (fz, gruppe) => {
                            const rfz = fzMap[fz.id];
                            const agg = aggregateFunktionen(rfz, gruppe.kuerzel);
                            const pctVal = row.anzahl_gesamt > 0 ? (agg.anzahl / row.anzahl_gesamt) * 100 : 0;
                            return (
                              <TableCell key={`${fz.id}-${gruppe.key}`} className={cn('text-right font-mono text-xs', KAT_LHF.bgColAlt)}>
                                {agg.anzahl > 0 ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <div className="font-semibold">{pct(pctVal)}</div>
                                        <div className="text-[10px] text-muted-foreground">
                                          <span className="text-amber-600">{agg.tag}</span>/<span className="text-indigo-600">{agg.nacht}</span>
                                        </div>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs font-semibold">{fz.funkrufname} · {gruppe.beschreibung || gruppe.label}</p>
                                      <p className="text-xs">{agg.anzahl}× gesamt · Tag {agg.tag} · Nacht {agg.nacht}</p>
                                      <p className="text-xs">{pct(pctVal)} der Gesamtdienste</p>
                                      <p className="text-xs text-muted-foreground">Funktionen: {gruppe.kuerzel.join(' + ')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : '—'}
                              </TableCell>
                            );
                          };
                          // Aggregat-Zelle für eine Kategorie (RD/LHF/Sonstige)
                          const renderKategorieAggregat = (kat, anzahl, prozent, anzahlTag, anzahlNacht, label, tooltipExtra) => (
                            <TableCell className={cn('text-right font-mono whitespace-nowrap border-l border-l-2', kat.border, kat.bgCol)}>
                              {anzahl > 0 ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <div className={cn('font-semibold', kat.accent)}>{pct(prozent)}</div>
                                      <div className="text-[10px] text-muted-foreground">
                                        {anzahl}×{(anzahlTag != null || anzahlNacht != null) && (
                                          <> (<span className="text-amber-600">{anzahlTag ?? '–'}</span>/<span className="text-indigo-600">{anzahlNacht ?? '–'}</span>)</>
                                        )}
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs font-semibold">{label}</p>
                                    <p className="text-xs">{anzahl}× ({pct(prozent)} aller Dienste)</p>
                                    {tooltipExtra}
                                  </TooltipContent>
                                </Tooltip>
                              ) : '—'}
                            </TableCell>
                          );
                          return (
                          <React.Fragment key={row.id}>
                            <TableRow
                              className={cn(
                                'cursor-pointer hover:bg-muted/50',
                                istErsterNichtNotSan && 'border-t-2 border-t-emerald-300/70'
                              )}
                              onClick={() => toggleRow(row.id)}
                              data-testid={`statistik-row-${row.id}`}
                            >
                              <TableCell className="sticky left-0 bg-background z-10">
                                {expandedRow === row.id
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />}
                              </TableCell>
                              <TableCell className="font-medium sticky left-8 bg-background z-10">{row.name}</TableCell>
                              <TableCell className="text-xs">{row.dienstgrad || '—'}</TableCell>
                              <TableCell>
                                {row.qualifikation && (
                                  <Badge variant="outline" className="text-[10px] whitespace-nowrap">
                                    {row.qualifikation}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                <div className="font-semibold">{row.anzahl_gesamt}</div>
                                <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                                  <span className="text-amber-600">{row.anzahl_tag}</span>
                                  <span className="text-muted-foreground">/</span>
                                  <span className="text-indigo-600">{row.anzahl_nacht}</span>
                                </div>
                              </TableCell>
                              {/* === RD === */}
                              {renderKategorieAggregat(
                                KAT_RD,
                                row.anzahl_rd,
                                row.prozent_rd,
                                row.anzahl_rd_tag,
                                row.anzahl_rd_nacht,
                                'Rettungsdienst',
                                <p className="text-xs">Hauptfunktion auf RTW/NEF/KTW</p>,
                              )}
                              {rdFzList.map(fz => renderFzCell(fz, KAT_RD.bgColAlt, 'haupt'))}
                              {/* === LHF === Sub-Funktionen NUR bei echten Löschfz */}
                              {lhfFzList.map((fz, idx) => (
                                <React.Fragment key={fz.id}>
                                  {renderFzCell(fz, cn(KAT_LHF.bgCol, 'font-semibold', idx === 0 && 'border-l border-l-2', idx === 0 && KAT_LHF.border), 'haupt')}
                                  {isEchtesLoeschfz(fz) && LHF_GRUPPEN.map(g => renderLhfGroupCell(fz, g))}
                                </React.Fragment>
                              ))}
                              {/* Springer-Spalten (kategorieübergreifend, orange Akzent) */}
                              {springerFzList.map(fz => renderFzCell(fz, cn(SPR_CELL, 'border-l text-orange-700'), 'springer'))}
                              {/* === Sonstige === direkt die Fahrzeuge (Haupt-Modus), kein Aggregat */}
                              {sonstigeFzList.map((fz, idx) => renderFzCell(
                                fz,
                                cn(KAT_SONSTIGE.bgColAlt, idx === 0 && 'border-l border-l-2', idx === 0 && KAT_SONSTIGE.border),
                                'haupt'
                              ))}
                              {/* Wandern (WA) */}
                              <TableCell className="text-right font-mono text-xs bg-pink-50/40 border-l border-l-2 border-pink-200">
                                {row.anzahl_wandern > 0 ? (
                                  <span className="text-pink-700 font-semibold">
                                    {row.anzahl_wandern} ({pct(row.prozent_wandern)})
                                  </span>
                                ) : '—'}
                              </TableCell>
                              {/* RTW-Wandern (RW) – eigene Spalte */}
                              <TableCell className="text-right font-mono text-xs bg-rose-100/40 border-l border-rose-300">
                                {(row.anzahl_rtw_wandern ?? 0) > 0 ? (
                                  <span className="text-rose-800 font-semibold">
                                    {row.anzahl_rtw_wandern} ({pct(row.prozent_rtw_wandern)})
                                  </span>
                                ) : '—'}
                              </TableCell>
                              {/* ZBV (unter Sonstiges) */}
                              <TableCell className="text-right font-mono text-xs bg-slate-100/40 border-l border-slate-300">
                                {(row.anzahl_zbv ?? 0) > 0 ? (
                                  <span className="text-slate-700 font-semibold">
                                    {row.anzahl_zbv} ({pct(row.prozent_zbv)})
                                  </span>
                                ) : '—'}
                              </TableCell>
                            </TableRow>

                            {/* Aufgeklappte Detail-Aufschlüsselung */}
                            {expandedRow === row.id && (
                              <TableRow data-testid={`statistik-row-detail-${row.id}`}>
                                <TableCell colSpan={totalCols} className="bg-muted/30 p-4">
                                  <ExpandedDetail mitarbeiter={row} fahrzeuge={data.fahrzeuge || []} />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </TooltipProvider>
    </MainLayout>
  );
}

// Aufgeklappter Detail-Bereich — gruppiert nach Kategorie (RD / LHF / Sonstige)
function ExpandedDetail({ mitarbeiter, fahrzeuge = [] }) {
  const { get: getFahrzeugBildByTyp } = useFahrzeugBilder();
  const fahrzeugById = useMemo(
    () => new Map((fahrzeuge || []).map(fahrzeug => [fahrzeug.id, fahrzeug])),
    [fahrzeuge]
  );
  const fahrzeugeMitDaten = (mitarbeiter.fahrzeuge || []).filter(fz => fz.anzahl_gesamt > 0);

  if (fahrzeugeMitDaten.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        Keine Fahrzeug-Einsätze im Zeitraum.
        {mitarbeiter.anzahl_wandern > 0 && (
          <span> Nur Wandern-Tage ({mitarbeiter.anzahl_wandern}×) eingetragen.</span>
        )}
      </p>
    );
  }

  // Map fahrzeug_id → Kategorie aus den globalen fahrzeug-Defs
  const katById = Object.fromEntries((fahrzeuge || []).map(f => [f.id, f.kategorie || 'Sonstige']));

  // Karten pro Kategorie gruppieren
  const groupedCards = { RD: [], LHF: [], Sonstige: [] };
  fahrzeugeMitDaten.forEach(fz => {
    const kat = katById[fz.fahrzeug_id] || 'Sonstige';
    (groupedCards[kat] || groupedCards.Sonstige).push(fz);
  });

  const renderCard = (fz) => {
    const fahrzeugDef = fahrzeugById.get(fz.fahrzeug_id);
    const typAusDefinition = fahrzeugDef?.typ || fahrzeugDef?.typ_extracted || '';
    const typAusFunkrufname = (fz.funkrufname || '').match(/^([A-Za-z]+)/)?.[1] || '';
    const typCode = (typAusDefinition || typAusFunkrufname || 'LHF').toUpperCase().split('-')[0];
    const imageUrl = getFahrzeugBildByTyp(typCode);
    const fzFarbe = getFahrzeugFarbe({
      typ: typCode,
      fahrzeugtyp_code: typCode,
      magnetboard_farbe: null,
    });
    const activeFunkt = (fz.funktionen || []).filter(f => f.anzahl_gesamt > 0);

    return (
      <div
        key={fz.fahrzeug_id}
        className={cn(
          "flex flex-col rounded border bg-card overflow-hidden",
          fz.ist_springer && "border-orange-300"
        )}
        data-testid={`fz-detail-${fz.fahrzeug_id}`}
      >
        {/* Header: SVG + Funkrufname + Gesamt */}
        <div
          className="flex items-center gap-1.5 px-1.5 py-1 border-b"
          style={{
            backgroundColor: fz.ist_springer ? 'rgb(255 247 237)' : `${fzFarbe}14`,
            borderColor: fz.ist_springer ? 'rgb(253 186 116)' : `${fzFarbe}33`,
          }}
        >
          <div
            className="w-8 h-5 shrink-0 rounded flex items-center justify-center overflow-hidden"
            title={fz.funkrufname}
          >
            <img
              src={imageUrl}
              alt={typCode}
              className="max-w-full max-h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className={cn(
              "font-semibold text-[11px] truncate leading-tight",
              fz.ist_springer && "text-orange-800"
            )}>{fz.funkrufname}</p>
            {fz.ist_springer && (
              <p className="text-[8px] text-orange-700 leading-tight font-semibold uppercase tracking-wider">Springer</p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs font-bold leading-none font-mono">{fz.anzahl_gesamt}×</p>
            <p className="text-[8px] text-muted-foreground font-mono leading-tight">{pct(fz.prozent_gesamt)}</p>
          </div>
        </div>

        {/* Funktions-Liste mit Tag/Nacht pro Funktion */}
        <div className="flex flex-col px-1.5 py-1 gap-0.5">
          {activeFunkt.length > 0 ? activeFunkt.map(f => {
            const hauptTag = f.haupt_tag || 0;
            const hauptNacht = f.haupt_nacht || 0;
            const springerTag = f.springer_tag || 0;
            const springerNacht = f.springer_nacht || 0;
            const hauptTotal = hauptTag + hauptNacht;
            const springerTotal = springerTag + springerNacht;

            const rows = [];
            // Haupt-Einsätze: kein Badge (Default)
            if (hauptTotal > 0) {
              rows.push({
                kind: 'haupt',
                label: 'Hauptfunktion (fest besetzt)',
                tag: hauptTag,
                nacht: hauptNacht,
              });
            }
            // Springer-Einsätze: orange Pill „Sprung"
            if (springerTotal > 0) {
              rows.push({
                kind: 'springer',
                label: 'Springer (besprungen)',
                tag: springerTag,
                nacht: springerNacht,
              });
            }

            return rows.map((r, idx) => (
              <Tooltip key={`${f.kuerzel}-${r.kind}`}>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-between gap-1 hover:bg-muted/40 rounded px-1 cursor-help">
                    <span className="flex items-center gap-1 min-w-0">
                      <span className={cn(
                        "font-mono text-[10px] font-medium truncate",
                        idx > 0 && "text-muted-foreground"
                      )}>
                        {idx === 0 ? f.kuerzel : ''}
                      </span>
                      {r.kind === 'springer' && (
                        <span
                          className="inline-flex items-center px-1 rounded-sm border text-[8px] font-bold leading-none uppercase tracking-wider bg-orange-100 text-orange-800 border-orange-300"
                          title={r.label}
                        >
                          Sprung
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1 font-mono text-[10px] tabular-nums shrink-0">
                      <span className="flex items-center gap-0.5 text-amber-600"><Sun className="w-2.5 h-2.5" />{r.tag}</span>
                      <span className="flex items-center gap-0.5 text-indigo-600"><Moon className="w-2.5 h-2.5" />{r.nacht}</span>
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs font-semibold">{f.name} · {r.label}</p>
                  <p className="text-xs">Tag {r.tag} · Nacht {r.nacht}</p>
                </TooltipContent>
              </Tooltip>
            ));
          }) : (
            <span className="text-[9px] text-muted-foreground italic text-center py-0.5">keine Funktion</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {KATEGORIEN.map(kat => {
        const cards = groupedCards[kat.key] || [];
        if (cards.length === 0) return null;
        return (
          <div key={kat.key} className={cn('rounded-lg border-l-4 pl-2', kat.border)}>
            <p className={cn('text-[11px] font-bold uppercase tracking-wider mb-1', kat.accent)}>
              {kat.label}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-1.5">
              {cards.map(renderCard)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default StatistikPage;
